import { getPreferredCurrency, setPreferredCurrency } from './currency';

/**
 * Static FX table for *display only* on the marketing pricing page.
 * Pricing is contracted in USD; this conversion just shows the user the
 * approximate equivalent in their local currency on first paint. It is
 * refreshed manually with each pricing review.
 *
 * Numbers are illustrative as of 2026-05.
 */
const USD_TO: Record<string, number> = {
  USD: 1.0,
  JPY: 152,
  EUR: 0.93,
  GBP: 0.80,
  CAD: 1.36,
  AUD: 1.51,
  NZD: 1.66,
  KRW: 1352,
  CNY: 7.20,
  HKD: 7.82,
  SGD: 1.35,
  TWD: 32.4,
  INR: 83.2,
  BRL: 5.0,
  MXN: 17.1,
  // Extended to cover every currency the geo detector can emit
  // (countryLocale/currencyForCountry), so a detected currency is never
  // silently downgraded to USD on the pricing page. Illustrative as of 2026-05.
  PHP: 58.0,
  IDR: 15800,
  VND: 25400,
  THB: 36.0,
  MYR: 4.70,
  ARS: 1010,
  CLP: 970,
  COP: 4350,
  PEN: 3.75,
  CHF: 0.88,
  SEK: 10.9,
  NOK: 11.0,
  DKK: 6.95,
  PLN: 4.00,
  CZK: 23.5,
  AED: 3.67,
  SAR: 3.75,
  ILS: 3.70,
  TRY: 34.5,
  RUB: 92.0,
  ZAR: 18.5,
  EGP: 49.0,
  NGN: 1650,
  KES: 129,
};

export function supportedDisplayCurrencies(): string[] {
  return Object.keys(USD_TO);
}

export function currentDisplayCurrency(): string {
  const pref = getPreferredCurrency();
  if (pref && USD_TO[pref] !== undefined) return pref;
  return 'USD';
}

export function setDisplayCurrency(code: string): void {
  setPreferredCurrency(code);
}

/**
 * Convert a USD amount to the user's display currency. Round to "nice"
 * numbers per currency (yen has no fractional units, etc.).
 */
export function fromUsd(amount: number, currency: string = currentDisplayCurrency()): { amount: number; currency: string } {
  const rate = USD_TO[currency] ?? 1;
  const raw = amount * rate;
  const rounded = roundForCurrency(raw, currency);
  return { amount: rounded, currency };
}

// Currencies with no commonly-used minor unit (or where fractional display is
// noise at these magnitudes): always rendered/rounded to whole numbers.
const ZERO_DECIMAL = new Set([
  'JPY', 'KRW', 'TWD', 'VND', 'IDR', 'CLP', 'COP',
]);

export function formatDisplayCurrency(amount: number, currency: string = currentDisplayCurrency(), locale = 'en-US'): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
}

function roundForCurrency(n: number, currency: string): number {
  if (ZERO_DECIMAL.has(currency)) {
    if (n >= 1000) return Math.round(n / 100) * 100;
    return Math.round(n);
  }
  if (n >= 50) return Math.round(n);
  return Math.round(n * 100) / 100;
}
